import * as React from 'react';
import { ifLoggedIn } from 'src/utils/navigation';

// Components imports
import { Desktop, Mobile } from 'src/components/TrackingInfo';
import Breadcrumbs from 'src/components/common/Breadcrumbs';
import DateRangeModel from 'src/model/dateRange.model';
import TablePaginator from 'src/components/common/TablePagination';
import { isMobile } from 'src/utils/responsiveHelpers';

// Models imports
import TrackingModel from 'src/model/trackingInfo.model';

// Style and misc imports
import './TrackingInfoView.scss';

interface ITrackingViewProps {
  t: any;
  trackingContent: TrackingModel[];
  pagination: IPagination;
  getTracking(filters: ITrackFilters, pagination: IPagination): void;
}

type SortType = 'ASC' | 'DESC' | 'AUTO';
interface IPagination {
  pageNumber: number;
  totalPages: number;
  pageSize: number;
  totalElements: number;
}

interface ITrackingViewState {
  filters: ITrackFilters;
  filterSelected: boolean;
  pageNumber: number;
  isMobile?: boolean;
}

interface ITrackFilters {
  dates: DateRangeModel | null;
  search: string;
  sortBy: string;
  sortIn: SortType;
}

class TrackingInfoView extends React.Component<
  ITrackingViewProps,
  ITrackingViewState
> {
  constructor(props: ITrackingViewProps) {
    super(props);

    this.state = {
      filters: {
        dates: null,
        search: '',
        sortBy: 'date',
        sortIn: 'DESC'
      },
      filterSelected: false,
      pageNumber: 0,
      isMobile: isMobile()
    };
  }

  public componentDidMount = () => {
    ifLoggedIn(() => {
      const { pagination } = this.props;
      this.props.getTracking(this.state.filters, pagination);
    });
  };

  private validateSearchText = (search: string) => {
    const voidStr = /[ ]/g;
    const matchArr = search.match(voidStr);
    if (matchArr && matchArr.length === search.length) {
      const filters = { ...this.state.filters, search: '' };
      this.setState({ filters });
      return '';
    }
    return search;
  };

  private setFilterSelected = (
    dates: DateRangeModel | null,
    search: string
  ) => {
    const validSearch = this.validateSearchText(search);
    return dates || validSearch ? true : false;
  };

  private handleDateFilter = (dates: DateRangeModel) => {
    const filters = { ...this.state.filters, dates };
    const filterSelected = this.setFilterSelected(
      dates,
      this.state.filters.search
    );
    this.setState({ filters, filterSelected }, () => {
      this.props.getTracking(this.state.filters, this.props.pagination);
    });
  };

  private handleSearchText = (e: any) => {
    const filters = { ...this.state.filters, search: e.target.value };
    this.setState({ filters });
  };

  private handleOnClickSearch = () => {
    const filterSelected = this.setFilterSelected(
      this.state.filters.dates,
      this.state.filters.search
    );
    this.setState({ filterSelected }, () => {
      this.props.getTracking(this.state.filters, this.props.pagination);
    });
  };

  private handleSort = (sortBy: string, sortIn: SortType) => {
    const setSortIn: SortType = sortIn === 'DESC' ? 'ASC' : 'DESC';
    const filters = { ...this.state.filters, sortBy, sortIn: setSortIn };
    this.setState({ filters }, () => {
      this.props.getTracking(this.state.filters, this.props.pagination);
    });
  };

  private handleClearFilters = () => {
    const { dates, search } = this.state.filters;
    if (dates || search) {
      const date: null = null;
      const searchClear: string = '';
      const filters = {
        ...this.state.filters,
        dates: date,
        search: searchClear
      };
      this.setState({ filters, filterSelected: false }, () => {
        this.props.getTracking(this.state.filters, this.props.pagination);
      });
    }
  };

  private handlePagination = (type: 'next' | 'previous') => {
    const { pageNumber, totalPages } = this.props.pagination;
    if (totalPages <= 1) {
      return;
    }

    if (type === 'previous') {
      if (pageNumber !== 0) {
        const page = pageNumber - 1;
        const filters = { ...this.state.filters, pageNumber: page };
        this.setState({ filters, pageNumber: page }, () => {
          const pagination = {
            ...this.props.pagination,
            pageNumber: this.state.pageNumber
          };
          this.props.getTracking(this.state.filters, pagination);
        });
      }
    } else {
      if (pageNumber !== totalPages - 1) {
        const page = pageNumber + 1;
        const filters = { ...this.state.filters, page };
        this.setState({ filters, pageNumber: page }, () => {
          const pagination = {
            ...this.props.pagination,
            pageNumber: this.state.pageNumber
          };
          this.props.getTracking(this.state.filters, pagination);
        });
      }
    }
  };

  private handleDesktopPagination = (pageNumber: number) => {
    if (pageNumber !== 0) {
      const page = pageNumber - 1;
      const filters = { ...this.state.filters, pageNumber: page };
      this.setState({ filters, pageNumber: page }, () => {
        const pagination = {
          ...this.props.pagination,
          pageNumber: this.state.pageNumber
        };
        this.props.getTracking(this.state.filters, pagination);
      });
    }
  };

  private setMobileFilters = (filters: ITrackFilters) => {
    this.setState({ filters }, () => {
      this.props.getTracking(this.state.filters, this.props.pagination);
    });
  };

  public handlePageSizeChange = (pageSize: any) => {
    const filters = { ...this.state.filters, pageNumber: 0 };
    this.setState({ filters, pageNumber: 0 }, () => {
      const pagination = {
        ...this.props.pagination,
        pageNumber: 0,
        pageSize: pageSize ? pageSize : 20
      };
      this.props.getTracking(this.state.filters, pagination);
    });
  };

  public render() {
    const { t, pagination, trackingContent } = this.props;
    return (
      <section id="rp-tracking-info">
        <Breadcrumbs>
          {t('common.home')} / {t('tracking.tracking_info')}
        </Breadcrumbs>
        <h1 className="page-title">{t('tracking.tracking_info')}</h1>
        <div className="tracking-container-desktop">
          <Desktop
            filters={this.state.filters}
            filterSelected={this.state.filterSelected}
            trackingContent={trackingContent}
            handleDateFilter={this.handleDateFilter}
            handleSearchText={this.handleSearchText}
            handleOnClickSearch={this.handleOnClickSearch}
            handleSort={this.handleSort}
            handleClearFilters={this.handleClearFilters}
            pagination={pagination}
            pageNumber={this.state.pageNumber}
            handleDesktopPagination={this.handleDesktopPagination}
            handlePageSizeChange={this.handlePageSizeChange}
          />
        </div>
        <div className="tracking-container-mobile">
          <Mobile
            trackings={trackingContent}
            pagination={pagination}
            filters={this.state.filters}
            handleSearchText={this.handleSearchText}
            handleOnClickSearch={this.handleOnClickSearch}
            setMobileFilters={this.setMobileFilters}
            handlePagination={this.handlePagination}
          />
        </div>
        <div className="rp-product-tracking-mobile-paginator">
          <TablePaginator
            handlePagination={this.handlePagination}
            pagination={pagination}
          />
        </div>
      </section>
    );
  }
}

export default TrackingInfoView;
